import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import PasswordTextField from "../components/form/fields/PasswordTextField";
import PasswordCheckTextField from "../components/form/fields/PasswordCheckTextField";
import Iconify from "../components/iconify/Iconify";
import { confirmPasswordReset } from "../app/api/axios/services/public.service";
import {
  getPasswordPolicyChecks,
  isPasswordPolicyValid,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_POLICY_RULES,
} from "../utils/passwordPolicy";

const STRENGTH_LEVELS = [
  { minScore: 0, label: "", color: "error" },
  { minScore: 1, label: "Sehr schwach", color: "error" },
  { minScore: 2, label: "Schwach", color: "warning" },
  { minScore: 3, label: "Mittel", color: "warning" },
  { minScore: 4, label: "Gut", color: "success" },
  { minScore: 5, label: "Stark", color: "success" },
];

const getStrength = (password) => {
  if (!password) {
    return { score: 0, label: "", color: "error", checks: {} };
  }

  const checks = getPasswordPolicyChecks(password);
  const score = Object.values(checks).filter(Boolean).length;
  const level = [...STRENGTH_LEVELS].reverse().find((entry) => score >= entry.minScore);
  return { score, checks, ...level };
};

function IconCircle({ icon, color, size = 72, iconSize = 34 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: alpha(color, 0.12),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 2,
      }}
    >
      <Iconify icon={icon} width={iconSize} sx={{ color }} />
    </Box>
  );
}

function CenteredLayout({ children }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg,
          ${alpha(theme.palette.primary.lighter, 0.55)} 0%,
          ${alpha(theme.palette.secondary.lighter, 0.35)} 100%)`,
        p: { xs: 2, sm: 3 },
      }}
    >
      {children}
    </Box>
  );
}

function MissingTokenState() {
  const theme = useTheme();
  return (
    <CenteredLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 5 },
            maxWidth: 480,
            width: "100%",
            borderRadius: 3,
            textAlign: "center",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <IconCircle icon="eva:alert-circle-fill" color={theme.palette.error.main} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Link ungültig
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
            Der Reset-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen
            Link über die Anmeldeseite an.
          </Typography>
          <Button component={RouterLink} to="/passwort-vergessen" variant="outlined">
            Neuen Link anfordern
          </Button>
        </Paper>
      </motion.div>
    </CenteredLayout>
  );
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { handleSubmit, control, watch } = useForm({
    defaultValues: { newPassword: "", checkPassword: "" },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const newPassword = watch("newPassword");
  const strength = useMemo(() => getStrength(newPassword), [newPassword]);
  const strengthColor = {
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    success: theme.palette.success.main,
  }[strength.color];

  const handleCheckMatch = (checkValue) => {
    if (watch("newPassword") !== checkValue) {
      return "Die Passwörter stimmen nicht überein.";
    }
    return true;
  };

  const onSubmit = async (data) => {
    if (!token) {
      setErrorMessage("Der Reset-Link ist ungültig oder abgelaufen.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    try {
      await confirmPasswordReset({
        token,
        newPassword: data.newPassword,
      });
      setIsSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (error) {
      const status = error?.response?.status;
      const backendMessage = error?.response?.data?.message;
      if (status === 429) {
        setErrorMessage(
          backendMessage ||
            "Zu viele Versuche. Bitte warten Sie kurz und versuchen Sie es erneut.",
        );
      } else {
        setErrorMessage(
          backendMessage ||
            "Das Passwort konnte nicht zurückgesetzt werden. Bitte fordern Sie einen neuen Link an.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return <MissingTokenState />;
  }

  if (isSuccess) {
    return (
      <CenteredLayout>
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              maxWidth: 480,
              width: "100%",
              borderRadius: 3,
              textAlign: "center",
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          >
            <IconCircle
              icon="eva:checkmark-circle-2-fill"
              color={theme.palette.success.main}
              size={78}
              iconSize={42}
            />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Passwort gespeichert
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
              Ihr neues Passwort wurde erfolgreich gesetzt.
            </Typography>
            <CircularProgress size={22} color="success" />
          </Paper>
        </motion.div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        style={{ width: "100%" }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 480, md: 860 },
            mx: "auto",
          }}
        >
          <Grid
            container
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: theme.customShadows.z24,
            }}
          >
            {!isMobile ? (
              <Grid
                item
                md={5}
                sx={{
                  background: `linear-gradient(155deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  p: 5,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <Iconify icon="eva:lock-fill" width={52} sx={{ mb: 3, opacity: 0.9 }} />
                <Typography variant="h4" fontWeight={800} sx={{ mb: 2, lineHeight: 1.25 }}>
                  Neues Passwort setzen
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.84, lineHeight: 1.75 }}>
                  Wählen Sie ein sicheres Passwort. Der Link aus der E-Mail kann nur
                  einmal verwendet werden und ist 30 Minuten gültig.
                </Typography>
              </Grid>
            ) : null}

            <Grid
              item
              xs={12}
              md={7}
              component={Paper}
              elevation={0}
              square
              sx={{ p: { xs: 3, sm: 5 }, borderRadius: { xs: 3, md: 0 } }}
            >
              {isMobile ? (
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <IconCircle icon="eva:lock-fill" color={theme.palette.primary.main} />
                </Box>
              ) : null}

              <Typography variant="h5" fontWeight={700} gutterBottom>
                Passwort neu festlegen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Bitte vergeben Sie ein neues Passwort und bestätigen Sie es.
              </Typography>

              {errorMessage ? <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert> : null}

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <PasswordTextField
                      name="newPassword"
                      label="Neues Passwort"
                      control={control}
                      fullWidth
                      additionalRules={{
                        validate: (value) =>
                          isPasswordPolicyValid(value) || PASSWORD_POLICY_MESSAGE,
                      }}
                    />

                    {newPassword?.length > 0 ? (
                      <Box sx={{ mt: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Passwortstärke
                          </Typography>
                          {strength.label ? (
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: strengthColor }}
                            >
                              {strength.label}
                            </Typography>
                          ) : null}
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(strength.score / PASSWORD_POLICY_RULES.length) * 100}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.grey[500], 0.16),
                            "& .MuiLinearProgress-bar": {
                              bgcolor: strengthColor,
                              borderRadius: 3,
                              transition: "width 0.4s ease",
                            },
                          }}
                        />
                        <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {PASSWORD_POLICY_RULES.map(({ key, label }) => {
                            const met = strength.checks?.[key];
                            return (
                              <Chip
                                key={key}
                                size="small"
                                label={label}
                                icon={
                                  <Iconify
                                    icon={
                                      met
                                        ? "eva:checkmark-circle-2-fill"
                                        : "eva:radio-button-off-fill"
                                    }
                                    width={13}
                                    sx={{ ml: 0.5 }}
                                  />
                                }
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 24,
                                  bgcolor: met
                                    ? alpha(theme.palette.success.main, 0.1)
                                    : alpha(theme.palette.grey[500], 0.1),
                                  color: met
                                    ? theme.palette.success.dark
                                    : theme.palette.text.secondary,
                                  "& .MuiChip-icon": { color: "inherit" },
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    ) : null}
                  </Grid>

                  <Grid item xs={12}>
                    <PasswordCheckTextField
                      name="checkPassword"
                      label="Passwort wiederholen"
                      control={control}
                      fullWidth
                      handleCheckMatch={handleCheckMatch}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <Iconify icon="eva:save-fill" width={18} />
                        )
                      }
                      sx={{ py: 1.5 }}
                    >
                      {isSubmitting ? "Wird gespeichert..." : "Neues Passwort speichern"}
                    </Button>
                  </Grid>
                </Grid>
              </form>

              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3 }}>
                <Typography
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Zur Anmeldung
                </Typography>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </motion.div>
    </CenteredLayout>
  );
}
